// Shared infrastructure for the staff, ticket, and admin pages: toasts, the app-wide
// confirm dialog, breadcrumbs, URL-backed filters with saved views, a date-range picker,
// sortable tables, and audit diffs. Markup uses the dashboard's own classes.
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { ApiError } from "../api";
import { Icon } from "../icons";

// Browser storage can be missing or throw (private windows, blocked site data); never let that break a page.
export function readStored<T>(key: string, fallback: T): T {
  try { const raw = window.localStorage.getItem(key); return raw === null ? fallback : JSON.parse(raw) as T; } catch { return fallback; }
}
export function writeStored(key: string, value: unknown) {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
}

/* ---------- Toasts and inline errors ---------- */

type ToastTone = "good" | "bad" | "info";
type ToastItem = { id: number; tone: ToastTone; message: string };
type ToastApi = (message: string, tone?: ToastTone) => void;
const ToastContext = createContext<ToastApi>(() => {});
export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(0);
  const dismiss = useCallback((id: number) => setItems((current) => current.filter((item) => item.id !== id)), []);
  const push = useCallback<ToastApi>((message, tone = "good") => {
    const id = ++next.current;
    setItems((current) => [...current.slice(-3), { id, tone, message }]);
    window.setTimeout(() => dismiss(id), tone === "bad" ? 9000 : 5000);
  }, [dismiss]);
  return <ToastContext.Provider value={push}>
    {children}
    <div className="toasts" role="region" aria-label="Notifications">
      {items.map((item) => <div key={item.id} className={`toast tone-${item.tone === "bad" ? "danger" : item.tone === "good" ? "success" : "info"}`} role={item.tone === "bad" ? "alert" : "status"}>
        <Icon name={item.tone === "bad" ? "alert" : item.tone === "good" ? "check" : "info"} size={16}/>
        <span>{item.message}</span>
        <button type="button" className="icon-button ghost" aria-label="Dismiss notification" onClick={() => dismiss(item.id)}><Icon name="close" size={14}/></button>
      </div>)}
    </div>
  </ToastContext.Provider>;
}

// Stays on the page until dismissed, so the details survive after the toast fades.
export function ErrorDetails({ error, onDismiss }: { error?: ApiError | null; onDismiss?: () => void }) {
  if (!error) return null;
  const detail = error.error || { code: "unknown_error", message: "Something went wrong.", requestId: "", retryable: true };
  return <div className="notice tone-danger error-details" role="alert">
    <div><strong>{detail.message || "Something went wrong."}</strong>
      <small>{detail.code?.replaceAll("_", " ")}{detail.requestId ? ` · request ${detail.requestId}` : ""}{detail.retryable ? " · safe to retry" : ""}</small>
      {detail.issues?.length ? <ul>{detail.issues.map((issue, index) => <li key={index}>{issue.path ? <code>{issue.path}</code> : null} {issue.message}</li>)}</ul> : null}
    </div>
    {onDismiss && <button type="button" className="icon-button ghost" aria-label="Dismiss error" onClick={onDismiss}><Icon name="close" size={14}/></button>}
  </div>;
}

export const asApiError = (error: unknown): ApiError => (error && typeof error === "object" && "error" in error ? error : { error: { code: "request_failed", message: error instanceof Error ? error.message : "The request failed.", requestId: "", retryable: true } }) as ApiError;

/* ---------- Confirm dialog ---------- */

export type ConfirmOptions = {
  title: string;
  impact: ReactNode[];
  confirmLabel: string;
  tone?: "danger" | "default";
  reason?: { label: string; minLength: number; placeholder?: string };
};
type ConfirmResult = { reason: string } | null;
type PendingConfirm = ConfirmOptions & { resolve: (result: ConfirmResult) => void };
const ConfirmContext = createContext<(options: ConfirmOptions) => Promise<ConfirmResult>>(async () => null);
export const useConfirm = () => useContext(ConfirmContext);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const ask = useCallback((options: ConfirmOptions) => new Promise<ConfirmResult>((resolve) => setPending({ ...options, resolve })), []);
  const close = (result: ConfirmResult) => { pending?.resolve(result); setPending(null); };
  return <ConfirmContext.Provider value={ask}>{children}{pending && <ConfirmDialog options={pending} onClose={close}/>}</ConfirmContext.Provider>;
}

function ConfirmDialog({ options, onClose }: { options: ConfirmOptions; onClose: (result: ConfirmResult) => void }) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const [reason, setReason] = useState("");
  const opener = useRef<Element | null>(null);
  useEffect(() => {
    opener.current = document.activeElement;
    panel.current?.querySelector<HTMLElement>("textarea, button.confirm-cancel")?.focus();
    return () => { if (opener.current instanceof HTMLElement) opener.current.focus(); };
  }, []);
  const valid = !options.reason || reason.trim().length >= options.reason.minLength;
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") { event.preventDefault(); onClose(null); return; }
    if (event.key !== "Tab" || !panel.current) return;
    const focusable = [...panel.current.querySelectorAll<HTMLElement>("button:not(:disabled), textarea")];
    const first = focusable[0], last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  return <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose(null)}>
    <div ref={panel} className={`dialog modal ${options.tone || "default"}`} role="alertdialog" aria-modal="true" aria-labelledby={titleId} onKeyDown={onKeyDown}>
      <h2 id={titleId}>{options.title}</h2>
      <div className="dialog-body"><p className="eyebrow">What will happen</p><ul>{options.impact.map((line, index) => <li key={index}>{line}</li>)}</ul></div>
      {options.reason && <div className="field"><label htmlFor={`${titleId}-reason`}>{options.reason.label}</label>
        <textarea id={`${titleId}-reason`} aria-describedby={`${titleId}-hint`} value={reason} placeholder={options.reason.placeholder} onChange={(event) => setReason(event.target.value)}/>
        <small id={`${titleId}-hint`}>{valid ? "Recorded in the audit log." : `At least ${options.reason.minLength} characters.`}</small></div>}
      <div className="dialog-actions">
        <button type="button" className="button confirm-cancel" onClick={() => onClose(null)}>Cancel</button>
        <button type="button" className={options.tone === "danger" ? "button danger" : "button primary"} disabled={!valid} onClick={() => onClose({ reason: reason.trim() })}>{options.confirmLabel}</button>
      </div>
    </div>
  </div>;
}

/* ---------- Breadcrumbs ---------- */

export type Crumb = { label: string; to?: string };
export function Breadcrumbs({ items }: { items: Crumb[] }) {
  return <nav className="breadcrumbs" aria-label="Breadcrumb"><ol>{items.map((item, index) => {
    const last = index === items.length - 1;
    return <li key={`${item.label}-${index}`}>{last ? <strong aria-current="page">{item.label}</strong> : item.to ? <Link to={item.to}>{item.label}</Link> : <span>{item.label}</span>}</li>;
  })}</ol></nav>;
}

/* ---------- URL-backed filter state ---------- */

export type UrlState<K extends string> = {
  values: Record<K, string>;
  set: (patch: Partial<Record<K | "page", string>>) => void;
  reset: () => void;
  active: boolean;
  query: string;
  apply: (query: string) => void;
};

// Filters live in the address bar so a filtered view can be bookmarked or shared.
// Any filter change sends the table back to its first page.
export function useUrlState<K extends string>(defaults: Record<K, string>): UrlState<K> {
  const [params, setParams] = useSearchParams();
  const keys = Object.keys(defaults) as K[];
  const values = Object.fromEntries(keys.map((key) => [key, params.get(key) ?? defaults[key]])) as Record<K, string>;
  const set = (patch: Partial<Record<K | "page", string>>) => setParams((current) => {
    const next = new URLSearchParams(current);
    if (!("page" in patch)) next.delete("page");
    for (const [key, value] of Object.entries(patch) as [string, string][]) {
      if (value === "" || value === (defaults as Record<string, string>)[key] || (key === "page" && value === "1")) next.delete(key); else next.set(key, value);
    }
    return next;
  }, { replace: true });
  const filterQuery = new URLSearchParams(keys.filter((key) => values[key] !== defaults[key]).map((key) => [key, values[key]])).toString();
  return {
    values,
    set,
    reset: () => setParams((current) => { const next = new URLSearchParams(current); for (const key of [...keys, "page"]) next.delete(key); return next; }, { replace: true }),
    active: Boolean(filterQuery),
    query: filterQuery,
    apply: (query: string) => setParams((current) => {
      const next = new URLSearchParams(current); const incoming = new URLSearchParams(query);
      for (const key of [...keys, "page"]) next.delete(key);
      for (const key of keys) { const value = incoming.get(key); if (value) next.set(key, value); }
      return next;
    }, { replace: true }),
  };
}

/* ---------- Date range ---------- */

export const RANGE_PRESETS = [
  { value: "", label: "Any time" },
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "custom", label: "Custom range" },
] as const;
const PRESET_HOURS: Record<string, number> = { "24h": 24, "7d": 168, "30d": 720 };
export const timeZoneName = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "local time"; } catch { return "local time"; } };

// Custom days are whole local calendar days, inclusive at both ends.
export function rangeBounds(values: { range?: string; from?: string; to?: string }, now = Date.now()): { start: number | null; end: number | null } {
  if (values.range && PRESET_HOURS[values.range]) return { start: now - PRESET_HOURS[values.range] * 3600000, end: null };
  if (values.range !== "custom") return { start: null, end: null };
  const day = (value?: string, endOfDay = false) => { if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null; const [y, m, d] = value.split("-").map(Number); return endOfDay ? new Date(y, m - 1, d, 23, 59, 59, 999).getTime() : new Date(y, m - 1, d).getTime(); };
  return { start: day(values.from), end: day(values.to, true) };
}
export function inRange(timestamp: string | null | undefined, values: { range?: string; from?: string; to?: string }, now = Date.now()) {
  const { start, end } = rangeBounds(values, now);
  if (start === null && end === null) return true;
  const time = timestamp ? Date.parse(timestamp) : NaN;
  if (Number.isNaN(time)) return false;
  return (start === null || time >= start) && (end === null || time <= end);
}
const rangeLabel = (values: { range?: string; from?: string; to?: string }) => values.range === "custom"
  ? `${values.from || "start"} to ${values.to || "today"}`
  : RANGE_PRESETS.find((preset) => preset.value === values.range)?.label || "Any time";

export function DateRangePicker({ values, onChange }: { values: { range: string; from: string; to: string }; onChange: (patch: { range?: string; from?: string; to?: string }) => void }) {
  return <fieldset className="date-range">
    <legend className="sr-only">Date range</legend>
    <label><span className="sr-only">Date range</span><Icon name="calendar" size={16}/>
      <select value={values.range} onChange={(event) => onChange(event.target.value === "custom" ? { range: "custom" } : { range: event.target.value, from: "", to: "" })}>
        {RANGE_PRESETS.map((preset) => <option key={preset.value} value={preset.value}>{preset.label}</option>)}
      </select></label>
    {values.range === "custom" && <>
      <label><span>From</span><input type="date" value={values.from} max={values.to || undefined} onChange={(event) => onChange({ from: event.target.value })}/></label>
      <label><span>To</span><input type="date" value={values.to} min={values.from || undefined} onChange={(event) => onChange({ to: event.target.value })}/></label>
    </>}
    <small className="fine-print" title="Dates and times on this page use your browser's time zone">Times in {timeZoneName()}</small>
  </fieldset>;
}

/* ---------- Filter bar with query chips and saved views ---------- */

export type FilterField = { key: string; label: string; options: Array<{ value: string; label: string }> };
type SavedView = { name: string; query: string };

export function FilterBar<K extends string>({ id, state, search, fields = [], dateRange = false, children }: {
  id: string;
  state: UrlState<K>;
  search?: { key: K; placeholder: string };
  fields?: FilterField[];
  dateRange?: boolean;
  children?: ReactNode;
}) {
  const values = state.values as Record<string, string>;
  const set = state.set as (patch: Record<string, string>) => void;
  const storageKey = `skyboxy.views.${id}`;
  const [views, setViews] = useState<SavedView[]>(() => readStored<SavedView[]>(storageKey, []));
  const [viewName, setViewName] = useState("");
  const saveViews = (next: SavedView[]) => { setViews(next); writeStored(storageKey, next); };
  const chips: Array<{ key: string; label: string; clear: Record<string, string> }> = [];
  if (search && values[search.key]) chips.push({ key: search.key, label: `Search: ${values[search.key]}`, clear: { [search.key]: "" } });
  for (const field of fields) if (values[field.key]) chips.push({ key: field.key, label: `${field.label}: ${field.options.find((option) => option.value === values[field.key])?.label || values[field.key]}`, clear: { [field.key]: "" } });
  if (dateRange && values.range) chips.push({ key: "range", label: `Date: ${rangeLabel(values)}`, clear: { range: "", from: "", to: "" } });
  const saveCurrent = () => {
    const name = viewName.trim().slice(0, 40);
    if (!name || !state.query) return;
    saveViews([...views.filter((view) => view.name !== name), { name, query: state.query }].slice(-12));
    setViewName("");
  };
  return <div className="filter-bar">
    <div className="toolbar" role="search">
      {search && <label className="search-field"><span className="sr-only">{search.placeholder}</span><Icon name="search" size={17}/><input type="search" value={values[search.key]} onChange={(event) => set({ [search.key]: event.target.value })} placeholder={search.placeholder}/></label>}
      {fields.map((field) => <label className="select-field" key={field.key}><span className="sr-only">{field.label}</span>
        <select value={values[field.key]} onChange={(event) => set({ [field.key]: event.target.value })}>
          <option value="">All {field.label.toLowerCase()}</option>
          {field.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select></label>)}
      {dateRange && <DateRangePicker values={{ range: values.range || "", from: values.from || "", to: values.to || "" }} onChange={(patch) => set(patch as Record<string, string>)}/>}
      {children}
      <details className="menu">
        <summary className="button compact"><Icon name="pin" size={14}/>Saved views{views.length ? ` (${views.length})` : ""}</summary>
        <div className="menu-panel">
          {views.length ? <ul className="view-list">{views.map((view) => <li key={view.name}>
            <button type="button" className={view.query === state.query ? "active" : ""} onClick={() => state.apply(view.query)}>{view.name}</button>
            <button type="button" className="icon-button danger-ghost" aria-label={`Delete saved view ${view.name}`} onClick={() => saveViews(views.filter((item) => item.name !== view.name))}><Icon name="trash" size={14}/></button>
          </li>)}</ul> : <p className="fine-print">No saved views yet. Set some filters, name them, and save.</p>}
          <div className="inline-form"><input aria-label="Name for this view" value={viewName} maxLength={40} placeholder="Name this view" onChange={(event) => setViewName(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); saveCurrent(); } }}/>
            <button type="button" className="button compact" disabled={!viewName.trim() || !state.query} onClick={saveCurrent}>Save view</button></div>
          {!state.query && <p className="fine-print">Choose at least one filter to save a view.</p>}
          <p className="fine-print">Views are saved in this browser only.</p>
        </div>
      </details>
      <button type="button" className="button compact" disabled={!state.active} onClick={state.reset}>Reset</button>
    </div>
    {chips.length > 0 && <ul className="chip-list filter-chips" aria-label="Active filters">{chips.map((chip) => <li key={chip.key}><button type="button" onClick={() => set(chip.clear)} aria-label={`Remove filter ${chip.label}`}>{chip.label}<Icon name="close" size={12}/></button></li>)}</ul>}
  </div>;
}

/* ---------- Data table ---------- */

export type Column<T> = {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  sortValue?: (row: T) => string | number | null | undefined;
  hideable?: boolean;
  defaultHidden?: boolean;
};
type TablePrefs = { density: "comfortable" | "compact"; hidden: string[]; pageSize: number };

export function compareValues(left: string | number | null | undefined, right: string | number | null | undefined) {
  const empty = (value: unknown) => value === null || value === undefined || value === "";
  if (empty(left) || empty(right)) return empty(left) === empty(right) ? 0 : empty(left) ? 1 : -1;
  if (typeof left === "number" && typeof right === "number") return left - right;
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" });
}

// Stable: rows that compare equal keep their original order in both directions; empty values always sort last.
export function stableSort<T>(rows: T[], value: (row: T) => string | number | null | undefined, direction: "asc" | "desc") {
  return rows.map((row, index) => ({ row, index, key: value(row) })).sort((a, b) => {
    const emptyA = a.key === null || a.key === undefined || a.key === "", emptyB = b.key === null || b.key === undefined || b.key === "";
    if (emptyA !== emptyB) return emptyA ? 1 : -1;
    const result = compareValues(a.key, b.key);
    return (direction === "desc" ? -result : result) || a.index - b.index;
  }).map((item) => item.row);
}

export function SortableTable<T>({ id, columns, rows, rowKey, empty, caption, pageSizes = [25, 50, 100] }: {
  id: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  empty: ReactNode;
  caption: string;
  pageSizes?: number[];
}) {
  const storageKey = `skyboxy.table.${id}`;
  const [prefs, setPrefsState] = useState<TablePrefs>(() => ({ density: "comfortable", hidden: columns.filter((column) => column.defaultHidden).map((column) => column.key), pageSize: pageSizes[0], ...readStored<Partial<TablePrefs>>(storageKey, {}) }));
  const setPrefs = (patch: Partial<TablePrefs>) => setPrefsState((current) => { const next = { ...current, ...patch }; writeStored(storageKey, next); return next; });
  const [params, setParams] = useSearchParams();
  const sortKey = params.get("sort") || "";
  const direction = params.get("dir") === "desc" ? "desc" : "asc";
  const visibleColumns = columns.filter((column, index) => index === 0 || !prefs.hidden.includes(column.key));
  const sortColumn = columns.find((column) => column.key === sortKey && column.sortValue);
  const sorted = useMemo(() => sortColumn?.sortValue ? stableSort(rows, sortColumn.sortValue, direction) : rows, [rows, sortColumn, direction]);
  const pageSize = pageSizes.includes(prefs.pageSize) ? prefs.pageSize : pageSizes[0];
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const page = Math.min(Math.max(1, Number(params.get("page")) || 1), pageCount);
  const pageRows = sorted.slice((page - 1) * pageSize, page * pageSize);
  const setParam = (patch: Record<string, string>) => setParams((current) => { const next = new URLSearchParams(current); for (const [key, value] of Object.entries(patch)) { if (value) next.set(key, value); else next.delete(key); } return next; }, { replace: true });
  const toggleSort = (column: Column<T>) => setParam(column.key !== sortKey ? { sort: column.key, dir: "", page: "" } : direction === "asc" ? { dir: "desc", page: "" } : { sort: "", dir: "", page: "" });
  const first = sorted.length ? (page - 1) * pageSize + 1 : 0;
  return <div className={`data-table density-${prefs.density}`}>
    <div className="data-table-toolbar">
      <p className="fine-print" role="status">{sorted.length ? `Showing ${first}–${first + pageRows.length - 1} of ${sorted.length}` : "No rows"}</p>
      <div className="data-table-controls">
        <div className="segmented" role="radiogroup" aria-label="Row density">
          {(["comfortable", "compact"] as const).map((density) => <button key={density} type="button" role="radio" aria-checked={prefs.density === density} onClick={() => setPrefs({ density })}>{density === "comfortable" ? "Comfortable" : "Compact"}</button>)}
        </div>
        <details className="menu">
          <summary className="button compact"><Icon name="layers" size={14}/>Columns</summary>
          <div className="menu-panel"><fieldset><legend>Show columns</legend>{columns.map((column, index) => {
            const locked = index === 0 || column.hideable === false;
            return <label key={column.key} className="check"><input type="checkbox" disabled={locked} checked={locked || !prefs.hidden.includes(column.key)} onChange={(event) => setPrefs({ hidden: event.target.checked ? prefs.hidden.filter((key) => key !== column.key) : [...prefs.hidden, column.key] })}/>{column.label}</label>;
          })}</fieldset></div>
        </details>
      </div>
    </div>
    {sorted.length ? <div className="table-wrap"><table>
      <caption className="sr-only">{caption}{sortColumn ? `, sorted by ${sortColumn.label} ${direction === "asc" ? "ascending" : "descending"}` : ""}</caption>
      <thead><tr>{visibleColumns.map((column) => <th key={column.key} scope="col" aria-sort={sortColumn?.key === column.key ? (direction === "asc" ? "ascending" : "descending") : column.sortValue ? "none" : undefined}>
        {column.sortValue ? <button type="button" className="sort-button" onClick={() => toggleSort(column)}>{column.label}<span aria-hidden="true" className="sort-mark">{sortColumn?.key === column.key ? (direction === "asc" ? "▲" : "▼") : "↕"}</span></button> : column.label}
      </th>)}</tr></thead>
      <tbody>{pageRows.map((row) => <tr key={rowKey(row)}>{visibleColumns.map((column) => <td key={column.key} data-label={column.label}>{column.render(row)}</td>)}</tr>)}</tbody>
    </table></div> : <div className="empty"><span className="empty-icon"><Icon name="search" size={22}/></span><h3>Nothing to show</h3><p>{empty}</p></div>}
    {sorted.length > pageSizes[0] && <nav className="pagination" aria-label={`${caption} pages`}>
      <button type="button" className="button compact" disabled={page <= 1} onClick={() => setParam({ page: page - 1 > 1 ? String(page - 1) : "" })}><Icon name="back" size={14}/>Previous</button>
      <span>Page {page} of {pageCount}</span>
      <button type="button" className="button compact" disabled={page >= pageCount} onClick={() => setParam({ page: String(page + 1) })}>Next<Icon name="arrow" size={14}/></button>
      <label><span>Rows per page</span><select value={pageSize} onChange={(event) => { setPrefs({ pageSize: Number(event.target.value) }); setParam({ page: "" }); }}>{pageSizes.map((size) => <option key={size} value={size}>{size}</option>)}</select></label>
    </nav>}
  </div>;
}

/* ---------- Audit diff ---------- */

const show = (value: unknown) => value === undefined ? "—" : typeof value === "string" ? value : JSON.stringify(value);
export function diffEntries(before: unknown, after: unknown, prefix = ""): Array<{ path: string; before: unknown; after: unknown }> {
  const isObject = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === "object" && !Array.isArray(value);
  if (isObject(before) && isObject(after)) {
    return [...new Set([...Object.keys(before), ...Object.keys(after)])].flatMap((key) => diffEntries(before[key], after[key], prefix ? `${prefix}.${key}` : key));
  }
  return JSON.stringify(before) === JSON.stringify(after) ? [] : [{ path: prefix || "value", before, after }];
}

export function AuditDiff({ before, after }: { before: unknown; after: unknown }) {
  if ((before === null || before === undefined) && (after === null || after === undefined)) return null;
  const changes = diffEntries(before ?? {}, after ?? {});
  if (!changes.length) return <p className="fine-print">No field changes were recorded.</p>;
  return <details className="audit-diff"><summary>{changes.length} field change{changes.length === 1 ? "" : "s"}</summary>
    <table><caption className="sr-only">Changed fields</caption><thead><tr><th scope="col">Field</th><th scope="col">Before</th><th scope="col">After</th></tr></thead>
      <tbody>{changes.slice(0, 50).map((change) => <tr key={change.path}><th scope="row"><code>{change.path}</code></th><td className="diff-old" data-label="Before"><del>{show(change.before)}</del></td><td className="diff-new" data-label="After"><ins>{show(change.after)}</ins></td></tr>)}</tbody>
    </table>{changes.length > 50 && <p className="fine-print">{changes.length - 50} more changes not shown.</p>}</details>;
}
